// Store-wide UI theme (boss sets in SystemSettings → applied on every device)
// + per-device override (cashier picks own theme via 🎨 ThemeSwitcher)
const KEY = "sellox_ui_theme_v1";
const DEVICE_KEY = "sellox_device_theme";
export const UI_THEMES = ["dark", "light", "clean", "cream", "eyecare", "midnight", "forest", "graphite", "sky", "contrast", "wechat"];

// Preview cards (shared by SystemSettings + ThemeSwitcher popup)
export const THEME_MODES = [
  { id: "dark", emoji: "🌙", names: { zh: "深色", es: "Oscuro", en: "Dark", pt: "Escuro" }, bg: "#0f172a", fg: "#ffffff", card: "#1e293b" },
  { id: "midnight", emoji: "🌌", names: { zh: "深蓝", es: "Azul noche", en: "Midnight", pt: "Azul" }, bg: "#0a1428", fg: "#dbeafe", card: "#101f3d" },
  { id: "forest", emoji: "🌲", names: { zh: "墨绿", es: "Bosque", en: "Forest", pt: "Floresta" }, bg: "#081c13", fg: "#d1fae5", card: "#0e2a1c" },
  { id: "graphite", emoji: "🌫", names: { zh: "柔和灰", es: "Gris suave", en: "Graphite", pt: "Cinza" }, bg: "#23272f", fg: "#f3f4f6", card: "#2e333d" },
  { id: "light", emoji: "☀️", names: { zh: "浅色", es: "Claro", en: "Light", pt: "Claro" }, bg: "#e7ebf0", fg: "#26323e", card: "#f3f5f7" },
  { id: "sky", emoji: "💠", names: { zh: "天蓝", es: "Cielo", en: "Sky", pt: "Céu" }, bg: "#e3edf7", fg: "#12263a", card: "#f4f8fc" },
  { id: "clean", emoji: "▫️", names: { zh: "简洁", es: "Sencillo", en: "Clean", pt: "Simples" }, bg: "#eef1f5", fg: "#1c2530", card: "#ffffff" },
  { id: "cream", emoji: "🤍", names: { zh: "极白", es: "Blanco ERP", en: "White ERP", pt: "Branco" }, bg: "#e9edf2", fg: "#1c2530", card: "#ffffff" },
  { id: "wechat", emoji: "💬", names: { zh: "微信风", es: "WeChat", en: "WeChat", pt: "WeChat" }, bg: "#ededed", fg: "#111111", card: "#ffffff" },
  { id: "eyecare", emoji: "🌿", names: { zh: "护眼绿", es: "Verde suave", en: "Eye care", pt: "Verde" }, bg: "#e2eee2", fg: "#1a2e22", card: "#f1f7ee" },
  { id: "contrast", emoji: "🔍", names: { zh: "大字高对比", es: "Alto contraste", en: "High contrast", pt: "Alto contraste" }, bg: "#000000", fg: "#ffd60a", card: "#0d0d0d" },
];
export const themeModeLabel = (m, lang) => `${m.emoji} ${(m.names && (m.names[lang] || m.names.es)) || ""}`;

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const v = parseInt(m[1], 16);
  return [v >> 16, (v >> 8) & 255, v & 255];
};
const shade = (hex, pct) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const f = pct / 100;
  const t = f > 0 ? 255 : 0;
  const p = Math.abs(f);
  return "#" + rgb.map((c) => clamp((t - c) * p + c).toString(16).padStart(2, "0")).join("");
};
const rgba = (hex, a) => {
  const rgb = hexToRgb(hex);
  return rgb ? `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})` : hex;
};

// Boss decision 2026-06: ALL icons stay BLUE regardless of theme
export const THEME_ICONS = {
  dark: "/brand/sellox-icon-blue.jpg",
  light: "/brand/sellox-icon-blue.jpg",
  cream: "/brand/sellox-icon-blue.jpg",
  clean: "/brand/sellox-icon-blue.jpg",
  eyecare: "/brand/sellox-icon-blue.jpg",
  graphite: "/brand/sellox-icon-blue.jpg",
  sky: "/brand/sellox-icon-blue.jpg",
  midnight: "/brand/sellox-icon-blue.jpg",
  forest: "/brand/sellox-icon-blue.jpg",
  contrast: "/brand/sellox-icon-blue.jpg",
};

export function themeIconUrl() {
  try {
    const th = document.documentElement.getAttribute("data-theme") || "dark";
    return THEME_ICONS[th] || THEME_ICONS.dark;
  } catch {
    return THEME_ICONS.dark;
  }
}

export function applyTheme(theme, accent, save = true) {
  const el = document.documentElement;
  const th = UI_THEMES.includes(theme) ? theme : "dark";
  if (th === "dark") el.removeAttribute("data-theme");
  else el.setAttribute("data-theme", th);
  try {
    const link = document.querySelector("link[rel='icon']");
    if (link) link.href = THEME_ICONS[th] || "/favicon.png";
    window.dispatchEvent(new CustomEvent("sellox-theme-changed", { detail: { theme: th } }));
  } catch { /* ignore */ }
  const vars = ["--sx-accent", "--sx-accent-strong", "--sx-accent-light", "--sx-accent-30", "--sx-accent-15"];
  if (accent && hexToRgb(accent)) {
    el.setAttribute("data-accent", "1");
    el.style.setProperty("--sx-accent", accent);
    el.style.setProperty("--sx-accent-strong", shade(accent, -18));
    el.style.setProperty("--sx-accent-light", shade(accent, 28));
    el.style.setProperty("--sx-accent-30", rgba(accent, 0.3));
    el.style.setProperty("--sx-accent-15", rgba(accent, 0.15));
  } else {
    el.removeAttribute("data-accent");
    vars.forEach((v) => el.style.removeProperty(v));
  }
  if (save) {
    try { localStorage.setItem(KEY, JSON.stringify({ theme: th, accent: accent || "" })); } catch { /* ignore */ }
  }
}

function storeCache() {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
}

// ---- Per-device display prefs: font scale + brightness (tenant adjustable) ----
const PREFS_KEY = "sellox_display_prefs";
export function getDisplayPrefs() {
  try { return { font: 100, bright: 0, clarity: 0, ...(JSON.parse(localStorage.getItem(PREFS_KEY) || "{}")) }; }
  catch { return { font: 100, bright: 0, clarity: 0 }; }
}
export function applyDisplayPrefs(p) {
  const prefs = p || getDisplayPrefs();
  const el = document.documentElement;
  if (prefs.font && Number(prefs.font) !== 100) el.style.fontSize = prefs.font + "%";
  else el.style.removeProperty("font-size");
  const clar = Number(prefs.clarity) || 0;
  if (clar > 0) el.style.setProperty("--sx-text-clarity", (clar * 0.28).toFixed(2) + "px");
  else el.style.removeProperty("--sx-text-clarity");
  let ov = document.getElementById("sx-dim-overlay");
  if (!ov) {
    ov = document.createElement("div");
    ov.id = "sx-dim-overlay";
    ov.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:2147483000;";
    document.body.appendChild(ov);
  }
  const b = Number(prefs.bright) || 0;
  ov.style.background = b === 0 ? "transparent" : (b > 0 ? `rgba(255,255,255,${(b / 130).toFixed(3)})` : `rgba(0,0,0,${(-b / 110).toFixed(3)})`);
}
export function setDisplayPrefs(partial) {
  const p = { ...getDisplayPrefs(), ...partial };
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
  applyDisplayPrefs(p);
  return p;
}

export function getDeviceTheme() {
  try { return localStorage.getItem(DEVICE_KEY) || ""; } catch { return ""; }
}

// Cashier picks own theme for THIS device ("" = follow store default)
export function setDeviceTheme(theme) {
  try {
    if (theme) localStorage.setItem(DEVICE_KEY, theme);
    else localStorage.removeItem(DEVICE_KEY);
  } catch { /* ignore */ }
  const c = storeCache();
  applyTheme(theme || c?.theme || "dark", c?.accent || "", false);
}

// Apply store default but respect device override; always cache store values
export function applyStoreTheme(theme, accent) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ theme: UI_THEMES.includes(theme) ? theme : "dark", accent: accent || "" }));
  } catch { /* ignore */ }
  applyTheme(getDeviceTheme() || theme, accent, false);
}

export function applyCachedTheme() {
  try {
    applyDisplayPrefs();
    const c = storeCache();
    const dev = getDeviceTheme();
    if (c || dev) applyTheme(dev || c?.theme, c?.accent, false);
  } catch { /* ignore */ }
}
